import { ref, type Ref } from 'vue'
import type { Customer, Organization, User } from '@/apiServices/types'
import { OrganizationAPI } from '@/apiServices/organizationAPI'
import { CustomerAPI } from '@/apiServices/customerAPI'
import userAPI from '@/apiServices/userAPI'
import { SiteInfoAPI } from '@/apiServices/siteInfoAPI'
import workOrderAPI from '@/apiServices/workOrderAPI'
import categoryAPI from '@/apiServices/categoryAPI'
import docTypeAPI from '@/apiServices/docTypeAPI'
import { AbstractPeriodicTask } from '@/generics/AbstractPeriodicTask'
import type { ISiteInfo } from '@/interfaces/ISiteInfo'

/**
 * A lookup entry as consumed by dropdowns and label resolution:
 * `{ label: "CODE - Name", value: "CODE", name: "Name" }`.
 *
 * `name` is carried separately so callers that already render the code (e.g. the
 * PM Calendar task-code chips) can append just the expansion instead of
 * re-splitting `label` on its separator.
 */
export interface CategoryOption {
  label: string
  value: string
  name: string
}

class GlobalConstantServiceClass extends AbstractPeriodicTask {

  /* =========================================================
      INTERNAL REACTIVE STATE (NEW)
     ========================================================= */
  private _userList: Ref<string[] | null> = ref(null)
  private _orgList: Ref<string[] | null> = ref(null)
  private _customerList: Ref<string[] | null> = ref(null)

  public orgInfoList: Ref<Organization[] | null> = ref(null)
  public customerInfoList: Ref<Customer[] | null> = ref(null)
  public siteInfoList: Ref<ISiteInfo[] | null> = ref(null)
  public userInfoList: Ref<User[] | null> = ref(null)

  // Lookup data from DB
  public tradeList: Ref<{ label: string; value: string }[]> = ref([])
  public categoryList: Ref<CategoryOption[]> = ref([])
  public jobTypeList: Ref<string[]> = ref([])
  public criticalityList: Ref<string[]> = ref([])
  public docTypeList: Ref<{ label: string; value: string }[]> = ref([])

  /* =========================================================
      SINGLETON
     ========================================================= */
  private static instance = new GlobalConstantServiceClass(300)

  private constructor(refreshInterval: number = 300) {
    super(refreshInterval)
  }

  public static getInstance(): GlobalConstantServiceClass {
    return GlobalConstantServiceClass.instance
  }

  /* =========================================================
      BACKWARD-COMPATIBLE GETTERS (UNCHANGED API)
     ========================================================= */

  // Legacy: returns non-reactive snapshot
  public getUsers(): string[] | null {
    return this._userList.value
  }

  public getOrgs(): string[] | null {
    return this._orgList.value
  }

  public getSiteInfo(): ISiteInfo[] | null {
    return this.siteInfoList.value
  }

  /* =========================================================
      NEW REACTIVE ACCESSORS (OPTIONAL FOR NEW CODE)
     ========================================================= */

  public get userList(): Ref<string[] | null> {
    return this._userList
  }

  public get orgList(): Ref<string[] | null> {
    return this._orgList
  }

  public get customerList(): Ref<string[] | null> {
    return this._customerList
  }

  /* =========================================================
      PERIODIC REFRESH
     ========================================================= */
  protected async run(): Promise<void> {

    const sessionToken = sessionStorage.getItem('sessionToken')

    if (!sessionToken) {
      this.stop()
      return
    }

    try {
      const [
        usersResponse,
        orgNamesResponse,

        siteInfoResponse,
        orgInfoResponse,
        userInfoResponse,
        customerInfoResponse,
        lookupData,
        categories,
        docTypes
      ] = await Promise.all([
        userAPI.getUserNames(),
        OrganizationAPI.getOrganizationNames(),

        SiteInfoAPI.getSiteInfo(),
        OrganizationAPI.getOrganizations(),
        userAPI.getUsers(),
        CustomerAPI.getCustomers(),
        workOrderAPI.getLookupData(),
        categoryAPI.getCategories(),
        docTypeAPI.getDocTypes()
      ])

      if (usersResponse.status === 200) {
        this._userList.value = usersResponse.data
      }

      if (orgNamesResponse.status === 200) {
        this._orgList.value = orgNamesResponse.data
      }

      if (siteInfoResponse.status === 200) {
        this.siteInfoList.value = siteInfoResponse.data
      }

      if (orgInfoResponse.status === 200) {
        this.orgInfoList.value = orgInfoResponse.data
      }

      if (userInfoResponse.status === 200) {
        this.userInfoList.value = userInfoResponse.data
      }

      if (customerInfoResponse.status === 200) {
        this.customerInfoList.value = customerInfoResponse.data
        this._customerList.value = customerInfoResponse.data.map(c => c.customerName)
      }

      if (lookupData) {
        this.tradeList.value = lookupData.trades.map(t => ({
          label: `${t.code} - ${t.name}`,
          value: t.code
        }))
        this.jobTypeList.value = lookupData.jobTypes
        this.criticalityList.value = lookupData.criticalities
        console.log('Lookup data loaded: trades=', this.tradeList.value.length,
          'jobTypes=', this.jobTypeList.value.length,
          'criticalities=', this.criticalityList.value.length)
      }

      if (categories) {
        this.categoryList.value = categories.map(c => ({
          label: `${c.eliCategory} - ${c.name}`,
          value: c.eliCategory,
          name: c.name
        }))
        console.log('Categories loaded:', this.categoryList.value.length)
      }

      if (docTypes) {
        this.docTypeList.value = docTypes.map(d => ({
          label: d.description,
          value: d.docTypeName
        }))
        console.log('Doc types loaded:', this.docTypeList.value.length)
      }

    } catch (err) {
      // Lookup data is refreshed opportunistically; callers handle empty lists.
      // Still log so failures (auth, network, 5xx) are visible in the console.
      console.warn('GlobalConstantService refresh failed:', err)
    }
  }
}

/* =========================================================
   EXPORTS (BACKWARD + REACTIVE)
   ========================================================= */

export const GlobalConstantService =
  GlobalConstantServiceClass.getInstance()

//  SAME NAMES AS BEFORE — NOW REACTIVE
export const UserList = GlobalConstantService.userList
export const OrgList = GlobalConstantService.orgList
export const CustomerList = GlobalConstantService.customerList

export const OrgInfoList = GlobalConstantService.orgInfoList
export const CustomerInfoList: Ref<Customer[] | null> = GlobalConstantService.customerInfoList
export const SiteInfoList = GlobalConstantService.siteInfoList
export const UserInfoList: Ref<User[] | null> = GlobalConstantService.userInfoList

// Lookup data from DB
export const TradeList = GlobalConstantService.tradeList
export const CategoryList = GlobalConstantService.categoryList
export const JobTypeList = GlobalConstantService.jobTypeList
export const CriticalityList = GlobalConstantService.criticalityList
export const DocTypeList = GlobalConstantService.docTypeList
